import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export type TomVendingLocationRow = {
  id: number;
  name?: string | null;
  isWarehouse?: boolean;
  is_warehouse?: boolean;
};

/** True when this row is the Tom Vending inventory warehouse hub (API may use camelCase or snake_case). */
export function tomVendingLocationIsWarehouse(
  loc: { isWarehouse?: boolean; is_warehouse?: boolean } | null | undefined,
): boolean {
  if (!loc) return false;
  return loc.isWarehouse === true || loc.is_warehouse === true;
}

/** Receive-at dropdown options: warehouses only, plus an existing non-warehouse selection when editing. */
export function tomVendingReceiveAtLocations(
  locations: TomVendingLocationRow[],
  includeLocationId?: number | null,
): TomVendingLocationRow[] {
  const warehouseLocs = locations.filter((l) => tomVendingLocationIsWarehouse(l));
  if (includeLocationId == null) return warehouseLocs;
  const current = locations.find((l) => l.id === includeLocationId);
  if (current && !warehouseLocs.some((l) => l.id === current.id)) {
    return [...warehouseLocs, current];
  }
  return warehouseLocs;
}

export async function fetchAllTomVendingLocations(): Promise<any[]> {
  const response = await httpClient.post('api/tom-vending/location/query', {
    skip: 0,
    take: 1000,
    search: '',
    orderBy: 'id',
    orderDirection: 'ASC',
  });
  return response.data.data || [];
}

export const useTomVendingLocationsList = () => {
  return useQuery({
    queryKey: ['tom-vending-locations'],
    queryFn: fetchAllTomVendingLocations,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateTomVendingLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/location/create', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-locations'] });
    },
  });
};

export const useTomVendingLocationDetails = (locationId: string | undefined) => {
  return useQuery({
    queryKey: ['tom-vending-location-details', locationId],
    queryFn: async () => {
      const response = await httpClient.get('api/tom-vending/location/details', {
        id: locationId,
      });
      return response.data;
    },
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useUpdateTomVendingLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/location/update', payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const id = variables?.id;
      if (id != null) {
        queryClient.invalidateQueries({ queryKey: ['tom-vending-location-details', String(id)] });
        queryClient.invalidateQueries({ queryKey: ['tom-vending-location-details', id] });
      }
      queryClient.invalidateQueries({ queryKey: ['tom-vending-locations'] });
    },
  });
};
